import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import AuthLayout from "./AuthLayout";
import { RESEND_SECONDS, errorText, postForm } from "../utils/aivioApi";

// Reset a demo account password: email a 6-digit code, then set a new password with that code.
// The API answers the same way whether or not the email has an account.
const MIN_PASSWORD = 8;
const MAX_PASSWORD = 72;

function ForgotPassword() {
  const [step, setStep] = useState("email"); // email | reset | done
  const [email, setEmail] = useState("");
  const [otpToken, setOtpToken] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const codeRef = useRef(null);

  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const timer = window.setTimeout(() => setResendIn((value) => value - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [resendIn]);

  useEffect(() => {
    if (step === "reset") codeRef.current?.focus();
  }, [step]);

  const sendCode = async () => {
    setBusy(true);
    setError("");
    try {
      const response = await postForm("forgot-password/send-code", { email });
      setOtpToken(response.data.otp_token);
      setNotice(response.data.message);
      setCode("");
      setResendIn(RESEND_SECONDS);
      setStep("reset");
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  const passwordProblem =
    password.length > 0 && password.length < MIN_PASSWORD ? `Use at least ${MIN_PASSWORD} characters.`
      : new TextEncoder().encode(password).length > MAX_PASSWORD ? `Use at most ${MAX_PASSWORD} characters.`
      : confirm.length > 0 && confirm !== password ? "The passwords don’t match."
      : "";
  const canReset = code.length === 6 && password.length >= MIN_PASSWORD && confirm === password && !passwordProblem;

  const resetPassword = async () => {
    setBusy(true);
    setError("");
    try {
      await postForm("forgot-password/reset", { email, otp_code: code, otp_token: otpToken, new_password: password });
      setStep("done");
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (busy) return;
    if (step === "email") sendCode();
    else if (step === "reset" && canReset) resetPassword();
  };

  const startOver = () => {
    setStep("email");
    setOtpToken("");
    setCode("");
    setPassword("");
    setConfirm("");
    setError("");
  };

  if (step === "done") {
    return (
      <AuthLayout title="Password updated" subtitle="Your Aivio password has been reset.">
        <div className="auth-form" aria-live="polite">
          <div className="otp-done"><span aria-hidden="true">✓</span>You can now log in with your new password. We’ve emailed you a confirmation.</div>
          <Link to="/login" className="btn auth-submit">Log in <span aria-hidden="true">→</span></Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Reset your password" subtitle={step === "email" ? "Enter the email you registered with and we’ll send you a 6-digit code." : "Enter the code we emailed you and choose a new password."}>
      <form onSubmit={handleSubmit} className="auth-form" noValidate={step === "reset"}>
        <div className="mb-3">
          <label className="form-label" htmlFor="reset-email">Work email</label>
          <input id="reset-email" type="email" className="form-control" value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com" autoComplete="email" readOnly={step !== "email"} required />
        </div>

        {step === "reset" && (
          <>
            <p className="otp-intro" aria-live="polite">{notice} It expires in 10 minutes.</p>
            <div className="mb-3">
              <label className="form-label" htmlFor="reset-code">Verification code</label>
              <input id="reset-code" ref={codeRef} className="form-control otp-input" type="text" inputMode="numeric" autoComplete="one-time-code" maxLength={6} placeholder="123456"
                value={code} onChange={(e) => { setCode(e.target.value.replace(/\D/g, "").slice(0, 6)); setError(""); }} />
              <div className="otp-actions">
                <button type="button" onClick={sendCode} disabled={busy || resendIn > 0}>{resendIn > 0 ? `Resend code in ${resendIn}s` : "Resend code"}</button>
                <button type="button" onClick={startOver} disabled={busy}>Use a different email</button>
              </div>
            </div>
            <div className="mb-3">
              <div className="auth-label-row">
                <label className="form-label" htmlFor="reset-password">New password</label>
                <button type="button" className="otp-change" onClick={() => setShowPassword((value) => !value)}>{showPassword ? "Hide" : "Show"}</button>
              </div>
              <input id="reset-password" type={showPassword ? "text" : "password"} className="form-control" value={password} onChange={(e) => { setPassword(e.target.value); setError(""); }}
                autoComplete="new-password" aria-describedby="reset-password-help" />
            </div>
            <div className="mb-2">
              <label className="form-label" htmlFor="reset-confirm">Confirm new password</label>
              <input id="reset-confirm" type={showPassword ? "text" : "password"} className="form-control" value={confirm} onChange={(e) => { setConfirm(e.target.value); setError(""); }} autoComplete="new-password" />
            </div>
            <p id="reset-password-help" className={passwordProblem ? "otp-error" : "otp-help"}>{passwordProblem || `At least ${MIN_PASSWORD} characters.`}</p>
          </>
        )}

        {error && <p className="otp-error" role="alert">{error}</p>}

        <button type="submit" className="btn auth-submit" disabled={busy || (step === "reset" && !canReset)}>
          {busy ? <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />{step === "email" ? "Sending code…" : "Resetting…"}</>
            : step === "email" ? <>Send code <span aria-hidden="true">→</span></> : <>Reset password <span aria-hidden="true">→</span></>}
        </button>
        <p className="auth-switch">Remembered it? <Link to="/login">Back to log in</Link></p>
      </form>
    </AuthLayout>
  );
}

export default ForgotPassword;
